import { api } from '../../lib/api.ts';
import { downloadFile } from '../../lib/download.ts';

export interface DhaftharuListRow {
  id: string;
  title: string;
  description: string | null;
  createdAt: string;
  conditions: unknown[] | null;
  columns: unknown[] | null;
  _count: { entries: number };
}

export interface ResidentResult {
  id: string;
  fullName: string;
  nationalId: string | null;
  sex: 'MALE' | 'FEMALE' | null;
}

export interface DhaftharuEntry {
  id: string;
  personId: string;
  orderIndex: number;
  person: {
    id: string;
    fullName: string;
    nationalId: string | null;
    sex: 'MALE' | 'FEMALE' | null;
    dateOfBirth: string | null;
  };
}

export interface Dhaftharu {
  id: string;
  title: string;
  description: string | null;
  entries: DhaftharuEntry[];
}

// ── Conditional dhaftharu types ──────────────────────────────────────────────

export type DhaftharuFieldType = 'TEXT' | 'NUMBER' | 'SELECT' | 'CHOICE' | 'MULTISELECT' | 'DATE';

export interface MetaField {
  id: string;
  label: string;
  type: DhaftharuFieldType;
  options: string[] | null;
}

export interface DhaftharuMeta {
  statuses: { value: 'ACTIVE' | 'MOVED_OUT' | 'DECEASED'; label: string }[];
  residentFields: MetaField[];
  registeredBusinessFields: MetaField[];
  homeBusinessFields: MetaField[];
  houseColumns: { attr: string; label: string }[];
}

export type BizEntity = 'REGISTERED_BUSINESS' | 'HOME_BUSINESS';

export interface Condition {
  source: 'STATUS' | 'RESIDENT_FIELD' | 'BUSINESS_FIELD';
  fieldId?: string;
  entity?: BizEntity;
  op: string;
  value?: string | number;
  value2?: string | number;
  values?: string[];
}

export interface ColumnSpec {
  source: 'FULLNAME' | 'STATUS' | 'HOUSE' | 'RESIDENT_FIELD' | 'BUSINESS_FIELD';
  attr?: 'name' | 'number' | 'address' | 'ward';
  fieldId?: string;
  entity?: BizEntity;
}

export interface ManualDhaftharu {
  id: string;
  title: string;
  description: string | null;
  kind: 'MANUAL';
  columns: ColumnSpec[] | null;
  entries: DhaftharuEntry[];
}

export interface ConditionalDhaftharu {
  id: string;
  title: string;
  description: string | null;
  kind: 'CONDITIONAL';
  conditions: Condition[];
  columns: ColumnSpec[] | null;
  columnLabels: string[];
  rows: string[][];
}

export type DhaftharuView = ManualDhaftharu | ConditionalDhaftharu;

/** Raw row returned by PATCH /dhaftharu/:id — no derived kind/rows/columnLabels. */
export interface DhaftharuRow {
  id: string;
  title: string;
  description: string | null;
  conditions: Condition[] | null;
  columns: ColumnSpec[] | null;
}

// ── API ───────────────────────────────────────────────────────────────────────

export const dhaftharuApi = {
  list: () => api.get<{ dhaftharu: DhaftharuListRow[] }>('/dhaftharu').then((r) => r.data.dhaftharu),
  create: (input: { title: string; description?: string; conditions?: Condition[]; columns?: ColumnSpec[] }) =>
    api.post<{ dhaftharu: DhaftharuListRow }>('/dhaftharu', input).then((r) => r.data.dhaftharu),
  get: (id: string) => api.get<{ dhaftharu: DhaftharuView }>(`/dhaftharu/${id}`).then((r) => r.data.dhaftharu),
  update: (
    id: string,
    patch: { title?: string; description?: string | null; conditions?: Condition[] | null; columns?: ColumnSpec[] | null },
  ): Promise<DhaftharuRow> =>
    api
      .patch<{ dhaftharu: DhaftharuRow }>(`/dhaftharu/${id}`, patch)
      .then((r) => r.data.dhaftharu),
  meta: () => api.get<DhaftharuMeta>('/dhaftharu/meta').then((r) => r.data),
  remove: (id: string) => api.delete(`/dhaftharu/${id}`).then((r) => r.data),
  searchResidents: (q: string) =>
    api.get<{ residents: ResidentResult[] }>('/dhaftharu/residents/search', { params: { q } }).then((r) => r.data.residents),
  addEntry: (id: string, personId: string) =>
    api.post(`/dhaftharu/${id}/entries`, { personId }).then((r) => r.data),
  removeEntry: (id: string, personId: string) =>
    api.delete(`/dhaftharu/${id}/entries/${personId}`).then((r) => r.data),
  reorder: (id: string, orderedEntryIds: string[]) =>
    api.post(`/dhaftharu/${id}/reorder`, { orderedEntryIds }).then((r) => r.data),
  generate: (id: string) => api.post(`/dhaftharu/${id}/generate`).then((r) => r.data),
  downloadPdf: (id: string): Promise<void> => downloadFile(`/dhaftharu/${id}/pdf`, `dhaftharu-${id}.pdf`),
};
